<script lang="ts">
  import { Button } from "$lib/components/ui/button";
  import { Input } from "$lib/components/ui/input";
  import * as Select from "$lib/components/ui/select/index.js";
  import { TriangleAlert } from "@lucide/svelte";
  import { t, type TranslationKey } from "$lib/i18n";
  import {
    type Zhihao,
    type ZhihaoPrefixDef,
    zhihaoPrefixId,
    findZhihaoPrefix,
    prefixesForDate,
    formatZhihao,
    withZhihaoRaw,
    withZhihaoDefaults,
    zhihaoMatchesDate,
    zhihaoUsesSetNotation,
    zhihaoUsesPosition,
  } from "@briefmarken_katalog/types";

  let {
    zhihao = $bindable<Zhihao | undefined>(undefined),
    dateOfIssue = null,
    isDerived = false,
    suggestedSetSize = undefined,
    setNotation = "editable",
    isReadonly = false,
    containerClass = "grid grid-cols-4 items-center gap-4",
  }: {
    zhihao: Zhihao | undefined;
    /** Drives which prefixes are offered first. */
    dateOfIssue?: Date | null;
    /** True for a stamp inside a set: the whole Zhihao is inherited, nothing is editable. */
    isDerived?: boolean;
    /** Set size to fall back on when none is entered — e.g. the set's stamp count. */
    suggestedSetSize?: number | undefined;
    /**
     * How much of the `(setSize-position)` notation this item may enter itself:
     * - `editable` — both, for items that stand on their own (block, booklet, se-tenants)
     * - `derivedPosition` — only the size; a set numbers its stamps, it has no position
     * - `omitted` — neither; a stamp only gets numbered once it sits in a set
     */
    setNotation?: "editable" | "derivedPosition" | "omitted";
    isReadonly?: boolean;
    containerClass?: string;
  } = $props();

  const NONE = "__none__";

  let isOpen = $state(false);
  let showAll = $state(false);

  const split = $derived(prefixesForDate(dateOfIssue));
  const selectedDef = $derived(zhihao ? findZhihaoPrefix(zhihao.prefix, zhihao.epoch) : undefined);
  const visiblePrefixes = $derived(showAll ? [...split.matching, ...split.other] : split.matching);

  // A prefix that was picked earlier (or imported) may sit outside the date's era —
  // reveal the full list so the current selection stays visible in the dropdown.
  $effect(() => {
    if (selectedDef && split.other.includes(selectedDef)) showAll = true;
  });

  const selectedId = $derived(selectedDef ? zhihaoPrefixId(selectedDef) : NONE);

  const showYear = $derived(selectedDef?.format === "Year" || selectedDef?.format === "Booklet");
  const showSetSize = $derived(
    !!selectedDef && setNotation !== "omitted" && zhihaoUsesSetNotation(selectedDef.format)
  );
  const showPosition = $derived(
    !!selectedDef && setNotation === "editable" && zhihaoUsesPosition(selectedDef.format)
  );

  // What will actually be stored: the gaps the surrounding item can fill are filled here,
  // so the preview never differs from the value the editor hands on.
  const effective = $derived(
    zhihao ? withZhihaoDefaults(zhihao, { setSize: suggestedSetSize }) : undefined
  );
  const preview = $derived(effective ? formatZhihao(effective) : "");
  const epochMismatch = $derived(!!zhihao && !zhihaoMatchesDate(zhihao, dateOfIssue));

  function prefixLabel(def: ZhihaoPrefixDef): string {
    return `${$t(def.labelKey as TranslationKey)} · ${$t(`zhihao.epochs.${def.epoch}` as TranslationKey)}`;
  }

  function apply(patch: Partial<Zhihao>) {
    const base: Zhihao = zhihao ?? { prefix: "", epoch: "Biannian" };
    zhihao = withZhihaoRaw({ ...base, ...patch });
  }

  function setNumber(field: "year" | "series" | "setSize" | "position", raw: string) {
    const trimmed = raw.trim();
    const parsed = Number(trimmed);
    const value = trimmed === "" || !Number.isFinite(parsed) ? undefined : parsed;
    apply({ [field]: value } as Partial<Zhihao>);
  }

  function selectPrefix(id: string) {
    if (id === NONE) {
      zhihao = undefined;
      return;
    }
    const def = visiblePrefixes.find((p) => zhihaoPrefixId(p) === id);
    if (def) apply({ prefix: def.code, epoch: def.epoch });
  }

  function clear() {
    zhihao = undefined;
    isOpen = false;
  }
</script>

{#if isReadonly}
  {#if zhihao}
    <div class="grid grid-cols-4 items-center gap-4">
      <span class="text-right text-sm font-medium">{$t('zhihao.button')}:</span>
      <span class="col-span-3 text-sm">{preview}</span>
    </div>
  {/if}
{:else if isDerived}
  <!-- A stamp of a set inherits its whole Zhihao and differs only in its position, which
       follows from its slot in the set — there is nothing left to edit here. -->
  <div class={containerClass}>
    <div class="col-span-4 flex items-center gap-2 text-sm">
      <span class="font-medium">{$t('zhihao.button')}:</span>
      <span>{preview || '—'}</span>
      <span class="text-xs text-muted-foreground">({$t('zhihao.inheritedFromSet')})</span>
    </div>
  </div>
{:else}
  <div class={containerClass}>
    <div class="col-span-4 relative">
      <Button variant="outline" type="button" onclick={() => isOpen = !isOpen}>
        {$t('zhihao.button')}{preview ? `: ${preview}` : ''}
      </Button>
      {#if isOpen}
        <div class="fixed inset-0 z-10" role="presentation" onclick={() => isOpen = false}></div>
        <div class="absolute left-0 bottom-full z-20 mb-1 w-96 rounded-md border bg-background shadow-md p-3 flex flex-col gap-2">
          <div class="flex items-center gap-2 text-sm">
            <span class="w-28 shrink-0 font-medium">{$t('zhihao.prefix')}</span>
            <Select.Root type="single" value={selectedId} onValueChange={selectPrefix}>
              <Select.Trigger class="flex-1 h-7 text-sm">
                {selectedDef ? prefixLabel(selectedDef) : '—'}
              </Select.Trigger>
              <Select.Content>
                <Select.Group>
                  <Select.Item value={NONE} label="—">—</Select.Item>
                  {#each visiblePrefixes as def}
                    <Select.Item value={zhihaoPrefixId(def)} label={prefixLabel(def)}>
                      {prefixLabel(def)}
                    </Select.Item>
                  {/each}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </div>
          {#if split.other.length > 0}
            <button
              type="button"
              class="self-start text-xs text-muted-foreground hover:text-foreground underline"
              onclick={() => showAll = !showAll}
            >
              {showAll ? $t('zhihao.showMatching') : `${$t('zhihao.showAll')} (${split.other.length})`}
            </button>
          {/if}

          {#if selectedDef}
            <div class="flex items-center gap-2 text-sm">
              {#if showYear}
                <span class="w-28 shrink-0 font-medium">{$t('zhihao.year')}</span>
                <Input
                  type="number"
                  class="w-24 h-7 text-sm"
                  value={zhihao?.year ?? ''}
                  oninput={(e) => setNumber('year', (e.target as HTMLInputElement).value)}
                />
              {/if}
              <span class="shrink-0 font-medium {showYear ? '' : 'w-28'}">{$t('zhihao.series')}</span>
              <Input
                type="number"
                class="w-24 h-7 text-sm"
                value={zhihao?.series ?? ''}
                oninput={(e) => setNumber('series', (e.target as HTMLInputElement).value)}
              />
            </div>
            {#if showSetSize || showPosition}
              <div class="flex items-center gap-2 text-sm">
                {#if showSetSize}
                  <span class="w-28 shrink-0 font-medium">{$t('zhihao.setSize')}</span>
                  <Input
                    type="number"
                    class="w-24 h-7 text-sm"
                    placeholder={suggestedSetSize ? String(suggestedSetSize) : ''}
                    value={zhihao?.setSize ?? ''}
                    oninput={(e) => setNumber('setSize', (e.target as HTMLInputElement).value)}
                  />
                {/if}
                {#if showPosition}
                  <span class="shrink-0 font-medium {showSetSize ? '' : 'w-28'}">{$t('zhihao.position')}</span>
                  <Input
                    type="number"
                    class="w-24 h-7 text-sm"
                    value={zhihao?.position ?? ''}
                    oninput={(e) => setNumber('position', (e.target as HTMLInputElement).value)}
                  />
                {/if}
              </div>
            {/if}
            {#if setNotation === 'derivedPosition' && showSetSize}
              <span class="text-xs text-muted-foreground">{$t('zhihao.positionPerStamp')}</span>
            {:else if setNotation === 'omitted' && zhihaoUsesPosition(selectedDef.format)}
              <span class="text-xs text-muted-foreground">{$t('zhihao.positionOnlyInSet')}</span>
            {/if}
          {/if}

          <div class="border-t pt-2 flex items-center justify-between gap-2 text-sm">
            <span>
              <span class="text-muted-foreground">{$t('zhihao.preview')}:</span>
              <span class="font-medium">{preview || '—'}</span>
            </span>
            {#if zhihao}
              <button
                type="button"
                class="shrink-0 text-xs text-muted-foreground hover:text-destructive underline"
                onclick={clear}
              >
                {$t('zhihao.clear')}
              </button>
            {/if}
          </div>

          {#if epochMismatch}
            <div class="flex items-start gap-1 text-xs text-amber-600">
              <TriangleAlert size={14} class="shrink-0 mt-0.5" />
              <span>{$t('zhihao.epochMismatch')}</span>
            </div>
          {/if}
        </div>
      {/if}
    </div>
  </div>
{/if}
